/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ApiParameter, get, post } from "@insite/client-framework/Services/ApiService";
import {
    FilterValue,
    MiscellaneousModel,
    PetProduct,
    PetPropertyModel,
    PetsType,
    PetType,
} from "@rc/App/Store/Reducers/xMyPets";

const petNumberOfVisitsURI = "api/preferencecenter/getpetnumberofvists";
const catBreedsURI = "api/preferencecenter/getcatbreeds";
const dogBreedsURI = "api/preferencecenter/getdogbreeds";
const catNeedsURI = "api/preferencecenter/getcatneeds";
const dogNeedsURI = "api/preferencecenter/getdogneeds";
const catProductsURI = "api/preferencecenter/getcatproducts";
const dogProductsURI = "api/preferencecenter/getdogproducts";
const dogSizesURI = "api/preferencecenter/getdogsizes";
const petBirthYearsURI = "api/preferencecenter/getpetbirthyears";
const petsBySubkeyURI = "api/preferencecenter/getpetsbysubkey";
const addUserPetDTOURI = "api/preferencecenter/adduserpetdto";
const updateUserPetDTOURI = "api/preferencecenter/updateuserpetdto";
const getPetDTOURI = "api/preferencecenter/getpet";

type PetNumberOfVisitsInformationResult = {
    successful?: boolean;
    error?: any;
    petNumberOfVists?: MiscellaneousModel[];
};

export async function getPetNumberOfVisits() {
    try {
        const result = await get<PetNumberOfVisitsInformationResult>(`${petNumberOfVisitsURI}`);
        return {
            successful: true,
            petNumberOfVists: result.petNumberOfVists || [],
        };
    } catch (error) {
        return {
            successful: false,
            petNumberOfVists: [],
        };
    }
}

type CatBreedsInformationResult = {
    successful: boolean;
    error?: any;
    breeds?: MiscellaneousModel[];
};

export async function getCatBreeds() {
    try {
        const result = await get<CatBreedsInformationResult>(`${catBreedsURI}`);
        return {
            successful: true,
            breeds: result.breeds || [],
        };
    } catch (error) {
        return {
            successful: false,
            breeds: [],
        };
    }
}

type DogBreedsInformationResult = {
    successful: boolean;
    error?: any;
    breeds?: MiscellaneousModel[];
};

export async function getDogBreeds() {
    try {
        const result = await get<DogBreedsInformationResult>(`${dogBreedsURI}`);
        return {
            successful: true,
            breeds: result.breeds || [],
        };
    } catch (error) {
        return {
            successful: false,
            breeds: [],
        };
    }
}

type CatNeedsInformationResult = {
    successful: boolean;
    error?: any;
    needs?: MiscellaneousModel[];
};

export async function getCatNeeds() {
    try {
        const result = await get<CatNeedsInformationResult>(`${catNeedsURI}`);
        return {
            successful: true,
            needs: result.needs || [],
        };
    } catch (error) {
        return {
            successful: false,
            needs: [],
        };
    }
}

type DogNeedsInformationResult = {
    successful: boolean;
    error?: any;
    needs?: MiscellaneousModel[];
};

export async function getDogNeeds() {
    try {
        const result = await get<DogNeedsInformationResult>(`${dogNeedsURI}`);
        return {
            successful: true,
            needs: result.needs || [],
        };
    } catch (error) {
        return {
            successful: false,
            needs: [],
        };
    }
}

type CatProductsInformationResult = {
    successful: boolean;
    error?: any;
    products: PetProduct[];
};

export async function getCatProducts() {
    try {
        const result = await get<CatProductsInformationResult>(`${catProductsURI}`);
        return {
            successful: true,
            products: result.products,
        };
    } catch (error) {
        return {
            successful: false,
            products: [],
        };
    }
}

type DogProductsInformationResult = {
    successful: boolean;
    error?: any;
    products: PetProduct[];
};

export async function getDogProducts() {
    try {
        const result = await get<DogProductsInformationResult>(`${dogProductsURI}`);
        return {
            successful: true,
            products: result.products,
        };
    } catch (error) {
        return {
            successful: false,
            products: [],
        };
    }
}

type DogSizesInformationResult = {
    successful: boolean;
    error?: any;
    sizes: FilterValue[];
};

export async function getDogSizes() {
    try {
        const result = await get<DogSizesInformationResult>(`${dogSizesURI}`);
        return {
            successful: true,
            sizes: result.sizes,
        };
    } catch (error) {
        return {
            successful: false,
            sizes: [],
        };
    }
}

type PetBirthYearsInformationResult = {
    successful: boolean;
    error?: any;
    years?: [];
};

export async function getPetBirthYears() {
    try {
        const result = await get<PetBirthYearsInformationResult>(`${petBirthYearsURI}`);
        return {
            successful: true,
            years: result.years || ([] as any),
        };
    } catch (error) {
        return {
            successful: false,
            years: [] as any,
        };
    }
}

type PetsBySubKeyInformationResult = {
    successful: boolean;
    error?: any;
    userPets?: PetsType;
};

export async function getPetsBySubKey(subscriberKey: any) {
    try {
        const result = await get<PetsBySubKeyInformationResult>(
            `${petsBySubkeyURI}?subscriberKeyQuery=${subscriberKey || ""}&time=${new Date().getTime()}`,
        );
        return {
            successful: true,
            userPets: result.userPets || ([] as any),
        };
    } catch (error) {
        return {
            successful: false,
            userPets: [] as any,
            error: true,
        };
    }
}

type PetInformationResult = {
    successful: boolean;
    error?: any;
    pet: PetType;
};

export async function getPet(subscriberKey: string | undefined, petKey: string | undefined) {
    try {
        const result = await get<PetInformationResult>(
            `${getPetDTOURI}?subscriberKeyQuery=${subscriberKey || ""}&petKey=${petKey}&time=${new Date().getTime()}`,
        );
        return {
            successful: true,
            pet: result.pet,
        };
    } catch (error) {
        return {
            successful: false,
            pet: {} as any,
        };
    }
}

export interface UpdatePetApiParameter extends ApiParameter {
    subscriberKey?: string;
    pet?: PetType;
    oldPetProperties?: any;
}

export async function updatePetsBySubKey(parameter: UpdatePetApiParameter): Promise<any> {
    try {
        const result = await post<any>(`${updateUserPetDTOURI}?subscriberKeyQuery=${parameter.subscriberKey || ""}`, {
            Pet: parameter.pet,
            OldPetProperties: parameter.oldPetProperties,
        });

        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
        };
    }
}

export interface AddPetApiParameter extends ApiParameter {
    subscriberKey?: string;
    pet?: PetType;
}

export async function addUserPet(parameter: AddPetApiParameter): Promise<any> {
    try {
        const result = await post<any>(`${addUserPetDTOURI}`, {
            subscriberKey: parameter.subscriberKey,
            Pet: parameter.pet,
        });

        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
        };
    }
}
